import React from "react";

import { clsx } from "clsx";

import { Lock } from "lucide-react";

import { twMerge } from "@/design-system/twMerge";

type CardAccent = "none" | "critical" | "warning" | "success" | "info";

const accentClasses: Record<Exclude<CardAccent, "none">, string> = {
  critical: "border-l-4 border-l-status-error",
  warning: "border-l-4 border-l-status-warning",
  success: "border-l-4 border-l-status-success",
  info: "border-l-4 border-l-status-info",
};

interface CardProps {
  className?: string;
  headerText?: string;
  headerClassName?: string;
  descriptionText?: string;
  icon?: React.ReactNode;
  headerAction?: React.ReactNode;
  locked?: boolean;
  children?: React.ReactNode;
  onClick?: () => void;
  clickable?: boolean;
  accent?: CardAccent;
}

export function CardSkeleton() {
  return (
    <div className="border-borderSubtle bg-surface-default flex w-full flex-col gap-5 rounded-lg border p-5 shadow">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 min-w-12 animate-pulse rounded bg-gray-200" />
        <div className="flex w-full justify-between gap-2">
          <div className="flex-1">
            <div className="mb-2 h-6 animate-pulse rounded bg-gray-200" />
            <div className="h-4 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      </div>
      <div className="pb-2">
        <div className="h-9 w-24 animate-pulse rounded bg-gray-200" />
      </div>
    </div>
  );
}

export function Card({
  className,
  headerText,
  headerClassName,
  descriptionText,
  icon,
  headerAction,
  locked,
  children,
  onClick,
  clickable,
  accent = "none",
}: CardProps) {
  const isClickable = clickable || !!onClick;

  return (
    <div
      className={twMerge(
        clsx(
          "border-borderSubtle bg-surface-default flex w-full flex-col justify-between gap-5 rounded-lg border p-5 shadow",
          children && "pb-2",
          isClickable &&
            "cursor-pointer transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none dark:hover:bg-gray-200",
          accent !== "none" && accentClasses[accent],
          className,
        ),
      )}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (!isClickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="flex items-center gap-3">
        {icon}
        <div className="flex w-full justify-between gap-2">
          <div>
            {headerText && (
              <h4
                className={twMerge(
                  "text-heading-md line-clamp-2",
                  headerClassName,
                )}
              >
                {headerText}
              </h4>
            )}
            {descriptionText && (
              <span className="text-body-sm text-textSubtle">
                {descriptionText}
              </span>
            )}
          </div>
          {(locked || headerAction) && (
            <div className="flex items-center gap-2 self-start">
              {locked && <Lock size={20} className="text-iconMinimal" />}
              {headerAction}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
